/**
 * Template-driven feature-component registry (SPEC PX-11 R2 - AC7).
 *
 * GENERALIZES the hardcoded VIEW_REGISTRY / VIEW_COMPONENTS that used to live
 * inline in `PortalView.tsx` into ONE registry keyed by a template's
 * enabled-views manifest. A tenant app COMPOSES the dialer's EXISTING `Portal*`
 * feature components from this registry + the tenant's branding - it forks NO
 * component and re-implements NO feature.
 *
 * Adding a shared feature to ALL tenant apps is ONE entry here (plus the backend
 * `PORTAL_VIEW_REGISTRY` entry), not a per-tenant fork. The order of
 * `FEATURE_REGISTRY` is the nav/tab order (mirrors the backend registry order).
 */

import type { ComponentType } from 'react'
import { PortalLeads } from '@/pages/portal/PortalLeads'
import { PortalPipeline } from '@/pages/portal/PortalPipeline'
import { PortalConversations } from '@/pages/portal/PortalConversations'
import { PortalResults } from '@/pages/portal/PortalResults'

export interface FeatureEntry {
  /** the view key (mirrors the backend PORTAL_VIEW_REGISTRY key) */
  key: string
  /** the nav-tab label */
  label: string
  /** the EXISTING Portal* component this view composes */
  component: ComponentType
}

/**
 * The shared feature registry. ONE entry per portal view; the component is the
 * EXISTING `Portal*` component (no fork). Order = nav order.
 */
export const FEATURE_REGISTRY: FeatureEntry[] = [
  { key: 'leads', label: 'Leads', component: PortalLeads },
  { key: 'pipeline', label: 'Pipeline', component: PortalPipeline },
  { key: 'conversations', label: 'Conversations', component: PortalConversations },
  { key: 'results', label: 'Results', component: PortalResults },
]

/** The registry-ordered list of all feature keys. */
export const FEATURE_KEYS: string[] = FEATURE_REGISTRY.map((f) => f.key)

/** Look up a feature entry by key (undefined if not registered). */
export function getFeature(key: string): FeatureEntry | undefined {
  return FEATURE_REGISTRY.find((f) => f.key === key)
}

/**
 * Resolve a template's enabled-views manifest into the registry-ordered list of
 * enabled feature keys. Drives a tenant app's nav + routing.
 *
 * @param enabledViews the explicit registry-ordered enabled_views list (from
 *   /api/me); preferred when present.
 * @param viewBools the four-bool portal_views map fallback for older payloads.
 */
export function resolveEnabledFeatures(
  enabledViews: string[] | undefined,
  viewBools: Record<string, boolean> | null | undefined
): string[] {
  if (enabledViews && enabledViews.length > 0) {
    return FEATURE_KEYS.filter((k) => enabledViews.includes(k))
  }
  if (!viewBools) return []
  return FEATURE_KEYS.filter((k) => viewBools[k])
}
